import { useCallback, useEffect, useMemo } from 'react';
import { dismissGoogleOneTap } from '../../../actions/google-one-tap-actions';
import { useAppDispatch, useAppSelector } from '../../../stores/redux.store';
import { SocialProvider } from '../../../types';
import { useAnalyticsClient } from '../../../utilities/analytics/analytics-web-client';
import { getCurrentCloudType } from '../../../utilities/env';
import {
  redirectToAppleLogin,
  redirectToGoogleLogin,
  redirectToMicrosoftLogin,
  redirectToSlackLogin,
  SocialLoginRedirectParams,
} from '../../../utilities/social-login';
import {
  getSocialLoginEligibility,
  SocialLoginEligibility,
} from '../../../utilities/social-login-form';
import { useRegisterComponent } from '../../orchestrator';
import { loginPageId } from '../constants';
import {
  useAppendReasonViewingScreen,
  useCobrandingApplication,
  useLoginRecoveryOptions,
  useSearchParamCodes,
} from './utility';
import { useDynamicConfigs } from '../../../utilities/feature-flags/dynamic-config';

type SocialLoginSupported = {
  envSupportsSocialLogin: true;
  socialLoginEligibility: SocialLoginEligibility;
  onAppleLogin: (_: string) => void;
  onSlackLogin: (_: string) => void;
  onMicrosoftLogin: (_: string) => void;
  onGoogleSocialLogin: (_: string) => void;
  onDismissGoogleOneTap: () => void;
  isGoogleOneTapDismissed: boolean;
  /**
   * Reflects whether social login is blocked for the user.
   *
   * Social Login options will be disabled if this is true
   *
   * */
  isSocialBlocked: boolean;
};

type SocialLoginNotSupported = {
  envSupportsSocialLogin: false;
  socialLoginEligibility: null;
  onAppleLogin: null;
  onSlackLogin: null;
  onMicrosoftLogin: null;
  onGoogleSocialLogin: null;
  onDismissGoogleOneTap: null;
  isGoogleOneTapDismissed: null;
  isSocialBlocked: boolean;
};

export const useSocialLogin = ({
  /**
   * Determines which redux slice the hook selects from:
   *   'v1' -> `state.login` (this is the default if unspecified)
   *   'v2' -> `state.loginV2`
   *
   * Consumer supplies this based on whether the login page targets `POST /check-username` or `POST
   * /v2/check-username`, respectively.
   *
   * The option is there to support migration from the former to the latter.
   *
   * It may be removed when the migration is complete across all login page implemenations.
   *
   * See README-login-v2.md for details.
   */
  loginStateSource = 'v1',
}: { loginStateSource?: 'v1' | 'v2' } = {}): SocialLoginSupported | SocialLoginNotSupported => {
  useRegisterComponent(useSocialLogin.name);

  const isCommercial = getCurrentCloudType() === 'commercial';

  const dispatch = useAppDispatch();
  const analyticsClient = useAnalyticsClient();
  const appendReasonViewingScreen = useAppendReasonViewingScreen();

  const isUsernameChecked = useAppSelector(state =>
    loginStateSource === 'v2' ? state.loginV2?.isUsernameChecked : state.login?.isUsernameChecked
  );
  const identities = useAppSelector(state => {
    if (loginStateSource === 'v2') {
      const selectedUserAction = state.loginV2?.selectedUserAction;
      return selectedUserAction?.action === 'social_login'
        ? selectedUserAction.identities
        : undefined;
    }

    return state.login?.identities;
  });
  const isLoginSecondStep = isUsernameChecked;
  const hasSocialIdentities =
    !!identities &&
    [identities.google, identities.microsoft, identities.apple, identities.slack].some(Boolean);
  const envSupportsSocialLogin = isCommercial || hasSocialIdentities; // if /v2/check-username returned social identities, assume they are allowed (HOT-301778)
  const isGoogleOneTapDismissed = useAppSelector(state => state?.googleOneTap?.dismissed);
  const isSocialBlocked = useAppSelector(state => {
    if (loginStateSource === 'v2') {
      const selectedUserAction = state.loginV2?.selectedUserAction;
      return (
        selectedUserAction?.action === 'no_action' && selectedUserAction.social === 'disallowed'
      );
    }

    return state.login?.isSocialBlocked;
  });
  const { errorCode } = useSearchParamCodes();

  const appConfig = useAppSelector(state => state.appConfig);
  const csrfToken = useAppSelector(state => state.csrfToken);

  const application = useCobrandingApplication();
  const dynamicConfigs = useDynamicConfigs();

  const socialLoginEligibility = useMemo(
    () => getSocialLoginEligibility(identities, application, dynamicConfigs),
    [application, identities, dynamicConfigs]
  );

  const { highlightedSocialProvider, isEligibleForSocialLoginOnly } = socialLoginEligibility;
  const showSocialLoginOnly =
    isLoginSecondStep &&
    isEligibleForSocialLoginOnly &&
    highlightedSocialProvider &&
    envSupportsSocialLogin;

  const socialButtonClickedEvent = useCallback(
    (socialProvider: SocialProvider) => {
      analyticsClient.buttonClickedEvent(
        showSocialLoginOnly ? 'socialLoginForm' : loginPageId,
        `${socialProvider}LoginButton`,
        appendReasonViewingScreen({})
      );
    },
    [analyticsClient, appendReasonViewingScreen, showSocialLoginOnly]
  );

  const loginRecoveryOptions = useLoginRecoveryOptions();

  const handleGoogleSocialLogin = useCallback(
    (email: string) => {
      socialButtonClickedEvent(SocialProvider.Google);

      redirectToGoogleLogin({
        location: window.location,
        source: loginPageId,
        recoveryOptions: loginRecoveryOptions(email),
      });
    },
    [loginRecoveryOptions, socialButtonClickedEvent]
  );

  const handleAuth0SocialLogin = useCallback(
    (
      socialProvider: SocialProvider,
      email: string,
      redirectFn: (params: SocialLoginRedirectParams) => void
    ) => {
      socialButtonClickedEvent(socialProvider);

      redirectFn({
        location: window.location,
        auth0Config: appConfig?.auth0Config,
        csrfToken: csrfToken ?? 'token-should-be-loaded',
        source: loginPageId,
        email: email || undefined, // explicitly pass undefined if empty to avoid including empty URL param
        recoveryOptions: loginRecoveryOptions(email),
      });
    },
    [appConfig?.auth0Config, csrfToken, loginRecoveryOptions, socialButtonClickedEvent]
  );

  const handleMicrosoftLogin = useCallback(
    (email: string) =>
      handleAuth0SocialLogin(SocialProvider.Microsoft, email, redirectToMicrosoftLogin),
    [handleAuth0SocialLogin]
  );

  const handleAppleLogin = useCallback(
    (email: string) => handleAuth0SocialLogin(SocialProvider.Apple, email, redirectToAppleLogin),
    [handleAuth0SocialLogin]
  );
  const handleSlackLogin = useCallback(
    (email: string) => handleAuth0SocialLogin(SocialProvider.Slack, email, redirectToSlackLogin),
    [handleAuth0SocialLogin]
  );

  const onDismissGoogleOneTap = useCallback(() => dispatch(dismissGoogleOneTap()), [dispatch]);

  const isCheckingUsername = useAppSelector(state =>
    loginStateSource === 'v2' ? state.loginV2?.isCheckingUsername : state.login?.isCheckingUsername
  );
  const isLoggingIn = useAppSelector(state =>
    loginStateSource === 'v2' ? state.loginV2?.isLoggingIn : state.login?.isLoggingIn
  );
  const usernameExists = useAppSelector(state => {
    if (loginStateSource === 'v2') {
      const selectedUserAction = state.loginV2?.selectedUserAction;
      return (
        selectedUserAction?.action !== 'signup' && selectedUserAction?.action !== 'not_permitted'
      );
    }

    return state.login?.usernameExists;
  });

  const isLoading = isCheckingUsername || isLoggingIn;

  useEffect(() => {
    if (!isLoading && isLoginSecondStep && usernameExists && identities) {
      analyticsClient.trackingEvent({
        page: loginPageId,
        action: 'triggered',
        subject: 'loginSecondStep',
        attributes: { eligibility: socialLoginEligibility },
      });
    }
  }, [
    analyticsClient,
    identities,
    isLoading,
    isLoginSecondStep,
    socialLoginEligibility,
    usernameExists,
  ]);

  if (envSupportsSocialLogin) {
    return {
      envSupportsSocialLogin,
      socialLoginEligibility,
      onAppleLogin: handleAppleLogin,
      onSlackLogin: handleSlackLogin,
      onMicrosoftLogin: handleMicrosoftLogin,
      onGoogleSocialLogin: handleGoogleSocialLogin,
      onDismissGoogleOneTap,
      isGoogleOneTapDismissed,
      isSocialBlocked: isSocialBlocked || errorCode?.code === 'social_login_banned',
    };
  }

  return {
    envSupportsSocialLogin: false,
    socialLoginEligibility: null,
    onAppleLogin: null,
    onSlackLogin: null,
    onMicrosoftLogin: null,
    onGoogleSocialLogin: null,
    onDismissGoogleOneTap: null,
    isGoogleOneTapDismissed: null,
    isSocialBlocked: false,
  };
};
